import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import AdrTentativeAgreementModalV3 from "../AdrTentativeAgreementModalV3";
import { SplitDropdown } from "../../_common/SplitDropdown";
import cem4907PdfActions from "../../../_actions/cem4907PdfActions";
import { AdrCorrectionCommentsV3 } from "./AdrCorrectionCommentsV3";
import { adrSections } from "../../../_constants/docSections";

export default function AdrReviewTentativeAgreementsV3(props) {
  const { tabId, projectId, status, markAdrClean } = props;

  const [showTaModal, _toggleTaModal] = useState(false);
  const toggleTaModal = () => _toggleTaModal(!showTaModal);

  const [clientId, setClientId] = useState({ value: 0, isNew: false });

  const dispatch = useDispatch();
  const notInitialRender = useRef(false);

  const adr = useSelector((state) => state.adr.openAdrs[tabId] || null);
  const sessionData = useSelector((state) =>
    state.adr.openAdrs[tabId] ? state.adr.openAdrs[tabId].sessionData || null : null
  );
  const projContractorList = useSelector(
    (state) => state.contractors.projContractorLists[projectId] || null
  );

  useEffect(
    () =>
      sessionData &&
      sessionData.newTaClientId &&
      setClientId({ value: sessionData.newTaClientId, isNew: true }),
    [sessionData]
  );

  const taList = useMemo(() => {
    if (adr && adr.tentativeAgreements && adr.tentativeAgreements.length > 0)
      return _.orderBy(adr.tentativeAgreements, "createdOn", "asc");
  }, [adr]);

  const filteredCcs = useMemo(
    () =>
      adr
        ? (adr.correctionComments || []).filter(
            (cc) => cc.section === adrSections.ADR_SECTION_TENTATIVEAGREEMENTS
          )
        : [],
    [adr.correctionComments]
  );

  /**
   *
   * @param {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient} ta
   * @param {Object.<number, number[]>} taSequences - keys are wpTabIds, values are arrays of ta.clientIds
   * @returns
   */
  const renderTa = (ta, taSequences) => {
    const isDisputed =
      ta.isDisputedByCaltransRep ||
      Object.values(ta.contractorsClient).some((cc) => cc.isDisputedByContractor);
    const isCaltransSigned = !isDisputed && ta.caltransRepSignedOn;
    const isContractorSigned =
      !isDisputed &&
      Object.values(ta.contractorsClient).some((cc) => cc.contractorRepSignedOn);
    const partialSigned =
      (isCaltransSigned && !isContractorSigned) ||
      (!isCaltransSigned && isContractorSigned);
    const bothSigned = isCaltransSigned && isContractorSigned;

    const labelBase =
      adr && adr.workPerformedClient[ta.wpTabId]
        ? adr.workPerformedClient[ta.wpTabId].wpShortCaption
        : "(Item/CO Desc. N/A)";
    const labelSeq =
      ta.wpTabId in taSequences && taSequences[ta.wpTabId].length > 1
        ? taSequences[ta.wpTabId].length
        : 0;
    const ctrId = Object.keys(ta.contractorsClient).length
      ? Object.keys(ta.contractorsClient)[0]
      : 0;
    const ctr = (projContractorList || []).find((c) => c.contractorId === +ctrId);
    const labelCtr = `${ctr ? ctr.name : "Ctr. N/A"}`;
    const labelSuffix = isDisputed
      ? "Disputed"
      : partialSigned
      ? "1/2 Signed"
      : bothSigned
      ? "2/2 Signed"
      : "Pending";
    const label = `${labelBase}${labelSeq ? ` (${labelSeq})` : ""} (${labelCtr})`;
    return (
      <SplitDropdown
        key={`taLinkKey_${ta.clientId}`}
        id={`ta_${ta.clientId}`}
        onClick={handleClick_ta(ta)}
        className="ta-list__item"
        color={
          isDisputed ? "warning" : partialSigned || bothSigned ? "success" : "secondary"
        }
        icon={partialSigned || bothSigned ? "check" : null}
        label={
          <span>
            <b>{label}</b> {`(${labelSuffix})`}
          </span>
        }
        menu={createDropdownMenu(ta)}
      />
    );
  };

  const renderContent = () => {
    // Track any multiple occurrences of TA for same WorkPerformed.
    /** @type {Object.<number, number[]>} */
    let taSequences = {}; // Keys are wpTabIds, values are clientIds for TA
    return (
      <div className="card">
        <div className="card-body p-2">
          <div className="flex flex-row flex-wrap ta-list">
            {(taList || []).length > 0
              ? taList.map((ta) => {
                  taSequences[ta.wpTabId] = [
                    ...(ta.wpTabId in taSequences ? taSequences[ta.wpTabId] : []),
                    ta.clientId,
                  ];
                  return renderTa(ta, taSequences);
                })
              : "There are no tentative agreements for this day."}
          </div>
        </div>
      </div>
    );
  };

  const handleClick_ta = (ta) => async () => {
    setClientId({ value: ta.clientId, isNew: false });
  };

  useEffect(() => {
    if (notInitialRender.current) {
      toggleTaModal();
    } else notInitialRender.current = true;
  }, [clientId]);

  const createDropdownMenu = useCallback(
    (ta) => [
      {
        label: "Open",
        icon: "folder-open",
        onClick: handleClick_ta(ta),
        show: true,
      },
      {
        label: "View TA (PDF)",
        icon: "file-pdf",
        onClick: () => dispatch(cem4907PdfActions.requestFromDb(ta.tentativeAgreementId)),
        show: true,
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <h4>
        Tentative Agreements
        <span className="h4 badge badge-secondary ml-2 align-middle">
          {adr && adr.tentativeAgreements ? adr.tentativeAgreements.length : "0"}
        </span>
      </h4>
      <div className="row">
        <div className="col-lg-9">{renderContent()}</div>
        <div className="col-lg-3 col-md-12 comment-column border-left">
          <AdrCorrectionCommentsV3
            section={adrSections.ADR_SECTION_TENTATIVEAGREEMENTS}
            correctionComments={filteredCcs}
            {...props}
          />
        </div>
      </div>
      {showTaModal && (
        <AdrTentativeAgreementModalV3
          tabId={tabId}
          projectId={projectId}
          status={status}
          clientId={clientId.value}
          isNew={clientId.isNew}
          markAdrClean={markAdrClean}
          showTaModal={showTaModal}
          toggleTaModal={toggleTaModal}
        />
      )}
    </React.Fragment>
  );
}
